import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dropdown, Popup } from "semantic-ui-react";
import { useAuth } from "../../contexts/AuthContext";
import { useCalls } from "../../contexts/CallsContext";
import { useInstallPrompt } from "../../contexts/InstallPromptContext";
import { useMyPresence } from "../../contexts/MyPresenceContext";
import { colors } from "../../style";
import { Avatar, Icon } from "../ui";

const ellipsis = {
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export const DOCS_URL = "https://docs.connect.cosoft.co.uk";

const presenceOptions = [
  { text: "Online", value: "online" },
  { text: "Busy", value: "busy" },
  { text: "DND", value: "dnd" },
];

// Opens upward in the sidebar footer, downward in the narrow toolbar
export function PresenceDropdown({ upward = true }) {
  const { presence, changePresence, getPresenceColor, getPresenceIcon } =
    useMyPresence();
  const { hasActiveCall } = useCalls();

  const handleChange = async (_, data) => changePresence(data.value);

  if (hasActiveCall() || presence === "in_a_call") {
    return (
      <Dropdown
        disabled
        button
        fluid
        upward={upward}
        className="icon red cx-presence"
        floating
        labeled
        icon="dont"
        options={[{ text: "In a call", value: "in_a_call" }]}
        value="in_a_call"
      />
    );
  }

  return (
    <Dropdown
      button
      fluid
      upward={upward}
      className={`ui form small icon ${getPresenceColor(presence)} cx-presence`}
      floating
      labeled
      icon={getPresenceIcon(presence)}
      options={presenceOptions}
      value={presence}
      onChange={handleChange}
    />
  );
}

export function ModeIndicator() {
  const { isPhoneControl } = useAuth();

  return (
    <Popup
      position="top center"
      content={`You are currently in ${
        isPhoneControl ? "phone control" : "softphone"
      } mode`}
      trigger={
        <span
          tabIndex={0}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "0 10px",
            fontSize: "12px",
            color: colors.textMuted,
            cursor: "default",
          }}
        >
          <Icon name={isPhoneControl ? "phone" : "headset"} size={14} />
          {isPhoneControl ? "Phone control mode" : "Softphone mode"}
        </span>
      }
    />
  );
}

const menuItem = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  width: "100%",
  padding: "9px 12px",
  border: 0,
  borderRadius: "8px",
  background: "none",
  color: colors.textStrong,
  font: "inherit",
  fontSize: "13px",
  textAlign: "left",
  textDecoration: "none",
  cursor: "pointer",
};

// Same button and menu as Sidekick web's UserButton: opens upward over the
// sidebar footer, closes on outside click or Escape
export function UserMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const { ipecsUser, apiUser } = useAuth();
  const { prompt, promptToInstall } = useInstallPrompt();
  const navigate = useNavigate();
  const name = ipecsUser.myInfo.name;

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => !ref.current?.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const go = (to) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <div ref={ref} style={{ position: "relative", marginTop: "8px" }}>
      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            bottom: "calc(100% + 6px)",
            left: 0,
            right: 0,
            padding: "6px",
            borderRadius: "10px",
            background: colors.dialog,
            border: `1px solid ${colors.borderStrong}`,
            boxShadow: "0 12px 32px rgba(0, 0, 0, 0.45)",
            zIndex: 20,
          }}
        >
          <div
            style={{
              ...ellipsis,
              padding: "6px 12px 8px",
              fontSize: "12px",
              color: colors.textMuted,
            }}
          >
            {apiUser?.email || name}
          </div>
          {prompt && (
            <button
              type="button"
              role="menuitem"
              className="sk-row"
              style={menuItem}
              onClick={() => {
                setOpen(false);
                promptToInstall();
              }}
            >
              <Icon name="download" size={15} />
              Install
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            className="sk-row"
            style={menuItem}
            onClick={() => go("/settings")}
          >
            <Icon name="settings" size={15} />
            Settings
          </button>
          <button
            type="button"
            role="menuitem"
            className="sk-row"
            style={menuItem}
            onClick={() => go("/whats-new")}
          >
            <Icon name="sparkle" size={15} />
            <AlertAnimation enabled={apiUser.whats_new}>
              What's New
            </AlertAnimation>
          </button>
          <a
            role="menuitem"
            className="sk-row"
            style={menuItem}
            target="_blank"
            rel="noopener noreferrer"
            href={DOCS_URL}
            onClick={() => setOpen(false)}
          >
            <Icon name="help" size={15} />
            Documentation
          </a>
          <button
            type="button"
            role="menuitem"
            className="sk-row"
            style={menuItem}
            onClick={() => go("/settings/logout")}
          >
            <Icon name="logout" size={15} />
            Log out
          </button>
        </div>
      )}
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        className="sk-card-button"
        onClick={() => setOpen(!open)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          width: "100%",
          padding: "10px",
          borderRadius: "10px",
          border: `1px solid ${colors.border}`,
          background: "none",
          color: "inherit",
          font: "inherit",
          textAlign: "left",
          cursor: "pointer",
        }}
      >
        <AlertAnimation enabled={apiUser.whats_new && !open}>
          <Avatar name={name} src={avatarUrl(ipecsUser.myInfo.avatar)} />
        </AlertAnimation>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            style={{
              ...ellipsis,
              fontSize: "13px",
              fontWeight: 600,
              color: colors.text,
            }}
          >
            {name}
          </span>
          {apiUser?.server?.name && (
            <span
              style={{
                ...ellipsis,
                fontSize: "12px",
                color: colors.textMuted,
              }}
            >
              {apiUser.server.name}
            </span>
          )}
        </span>
        <Icon name="chevronDown" size={14} color={colors.textMuted} />
      </button>
    </div>
  );
}

// iPECS sends "initial:AB" when there's no photo; Avatar draws initials then
const avatarUrl = (src) => (src && !src.startsWith("initial") ? src : null);

export function AlertAnimation({ enabled, children }) {
  if (!enabled) return children;

  return (
    <motion.div
      animate={{ y: [-10, 0] }}
      transition={{
        type: "spring",
        repeat: Infinity,
        stiffness: 500,
        duration: 1,
        repeatDelay: 1,
      }}
    >
      {children}
    </motion.div>
  );
}
